import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { deleteServiceProviders, exportToExcel, sendEmail } from "../../api";
import { DeleteRecord, EditRecord, ExportRecord, Pagination } from "../core";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import DetailsModal from "../modals/DetailsModal";
import { setNotification } from "../../redux/notificationSlice";

export const SearchVendorResult = ({ searchResult }) => {
  const [verifiedData, setVerifiedData] = useState([]);
  const [deleteSPModal, setDeleteSPModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(verifiedData.length / itemsPerPage);
  const currentItems = verifiedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userInfo = useSelector((state) => state?.user?.profile);
  useEffect(() => {
    setVerifiedData(searchResult || []);
  }, [searchResult]);
  const onCloseDetails = () => {
    setShowModal(false);
  };

  const openSPDeleteModal = (data) => {
    setDeleteSPModal(true);
    setRowToDelete(data);
  };
  const deleteSPRow = async () => {
    const dataToDelete = {
      operation: "removeServiceProvider",
      request: { id: rowToDelete.id, username: userInfo.isid },
    };
    const deleteResp = await deleteServiceProviders(dataToDelete);
    if (deleteResp.responseCode === 200) {
      setVerifiedData(
        verifiedData.filter((item) => item.id !== rowToDelete.id)
      );
      setDeleteSPModal(false);
      const emailBody = `<div className="bg-gray-100 p-8 font-sans">
        <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
          <p className="mb-4">Dear ${rowToDelete.primaryName},</p>
          <p className="mb-2">
            The service provider details with Request ID # ${rowToDelete.requestId} is deleted from the system.
          </p>
          <p className="mb-4">Please do not reply to this email as this is an automated email.</p>
          <p className="mb-4">
            Thanks!</br>
            MSD GCTO Service Portal
          </p>
        </div>
      </div>`;
      const deleteEmailSubject =
        "[MSD GCTO Service Provider Portal] Service Provider Data Deleted";
      sendEmail(deleteEmailSubject, emailBody, [rowToDelete.primaryEmail]);
      setRowToDelete(null);
      dispatch(
        setNotification({
          type: "success",
          show: true,
          message: "Success! service provider deleted successfully",
        })
      );
    }
  };

  const updateServiceProvider = (data) => {
    navigate("/update-serviceprovider", { state: data });
  };

  const showDetailsModal = (data) => {
    setShowModal(true);
    setDetails(data);
  };

  return (
    <>
      {Array.isArray(verifiedData) && verifiedData.length > 0 && (
        <>
          <div className="text-sm font-medium text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
            <ul className="flex flex-wrap -mb-px justify-between w-full">
              <li className="me-2">
                <h1 className="text-lg font-medium">Search Result</h1>
              </li>
              <li className="ml-auto">
                <button
                  onClick={exportToExcel}
                  className="inline-flex p-4 items-center justify-center border-b-2 border-transparent rounded-t-lg hover:text-sky-800 hover:border-sky-800 dark:hover:text-gray-800 group"
                >
                  <ExportRecord />
                  Export Complete Service Provider Data
                </button>
              </li>
            </ul>
          </div>
          <div className="w-full max-h-[30rem] mb-10">
            <table className="table-fixed border border-slate-400 w-full">
              <thead className="bg-gray-300 text-left">
                <tr className="border border-slate-300">
                  <th className="p-1">Service Provider Name</th>
                  <th className="p-1">Service Provider Url</th>
                  <th className="p-1">Request ID</th>
                  <th className="p-1">Primary Name</th>
                  <th className="p-1">Primary Email</th>
                  {userInfo && userInfo.role !== "Read" ? (
                    <th className="p-1"></th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {currentItems?.map((row) => (
                  <tr key={row.id} className="border border-slate-300">
                    <td className="px-4 py-2">
                      <button
                        onClick={() => showDetailsModal(row)}
                        className="underline text-blue-600 hover:text-blue-800 visited:text-purple-600"
                      >
                        {row.serviceProviderName}
                      </button>
                    </td>
                    <td className="py-2">{row.serviceProviderURL}</td>
                    <td className="py-2">{row.requestId}</td>
                    <td className="py-2">{row.primaryName}</td>
                    <td className="py-2">{row.primaryEmail}</td>
                    {userInfo && userInfo.role !== "Read" ? (
                      <td className="p-2 text-right">
                        <button
                          className="text-white px-2 py-px"
                          value={row.id}
                          data-testid="editsp"
                          onClick={() => updateServiceProvider(row)}
                        >
                          <EditRecord />
                        </button>
                        <button
                          className="text-white px-2 py-px ml-2"
                          value={row.id}
                          data-testid="deletesp"
                          onClick={() => openSPDeleteModal(row)}
                        >
                          <DeleteRecord />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            {verifiedData.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
          <DetailsModal
            showModal={showModal}
            onClose={() => onCloseDetails()}
            details={details}
          />
          <ConfirmationModal
            isOpen={deleteSPModal}
            onClose={() => setDeleteSPModal(false)}
            onDelete={() => deleteSPRow()}
            header="Confirm delete Service Provider"
            message="Are you sure you want to delete? This action cannot be undone."
          />
        </>
      )}
    </>
  );
};

SearchVendorResult.propTypes = {
  searchResult: PropTypes.object,
};
